import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/asignacion";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const nombreInstructor = (instructor) => {
  if (!instructor || typeof instructor !== "object") return "—";

  return [
    instructor.nombre1Instructor,
    instructor.nombre2Instructor,
    instructor.apellido1Instructor,
    instructor.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || "—";
};

const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime())
    ? "—"
    : fecha.toLocaleDateString("es-CO");
};

const Auditoria_ListarAsignacion = () => {
  const navigate = useNavigate();

  const [registros, setRegistros] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("");
  const [loading, setLoading] = useState(false);

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => navigate("/login")
        );
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(API, authConfig());
      setRegistros(
        Array.isArray(data?.asignaciones) ? data.asignaciones : []
      );
    } catch (error) {
      manejarError(error, "No se pudieron cargar las asignaciones.");
    } finally {
      setLoading(false);
    }
  }, [manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtrados = useMemo(() => {
    const texto = buscar.trim().toLowerCase();

    return registros.filter((item) => {
      const instructor = item.instructorObjectId;

      const coincideTexto =
        !texto ||
        [
          nombreInstructor(instructor),
          instructor?.documentoInstructor,
          item.auditorObjectId,
          item.observacionAsignacion,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(texto);

      return (
        coincideTexto &&
        (!estado || item.estadoAsignacion === estado)
      );
    });
  }, [registros, buscar, estado]);

  const finalizar = async (item) => {
    const confirmar = await Swal.fire({
      title: "¿Finalizar asignación?",
      text: nombreInstructor(item.instructorObjectId),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Finalizar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmar.isConfirmed) return;

    try {
      await axios.patch(
        `${API}/${item._id}/finalizar`,
        {},
        authConfig()
      );

      await cargar();
      Swal.fire("Correcto", "Asignación finalizada.", "success");
    } catch (error) {
      manejarError(error, "No se pudo finalizar la asignación.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">Asignaciones de auditoría</h3>
          <p className="text-muted mb-0">
            Relación entre auditores e instructores.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => navigate("/auditoria/asignacion/nueva")}
        >
          Registrar asignación
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <input
                className="form-control"
                placeholder="Buscar instructor, documento o auditor ObjectId..."
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <select
                className="form-select"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="Activo">Activo</option>
                <option value="Finalizado">Finalizado</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Instructor</th>
                  <th>Documento</th>
                  <th>Auditor ObjectId</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Estado</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      Cargando...
                    </td>
                  </tr>
                ) : filtrados.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No hay asignaciones.
                    </td>
                  </tr>
                ) : (
                  filtrados.map((item) => (
                    <tr key={item._id}>
                      <td>{nombreInstructor(item.instructorObjectId)}</td>
                      <td>
                        {item.instructorObjectId?.documentoInstructor || "—"}
                      </td>
                      <td>
                        <code>{String(item.auditorObjectId || "—")}</code>
                      </td>
                      <td>{formatearFecha(item.fechaInicioAsignacion)}</td>
                      <td>{formatearFecha(item.fechaFinAsignacion)}</td>
                      <td>{item.estadoAsignacion || "—"}</td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate(
                                `/auditoria/asignacion/editar/${item._id}`
                              )
                            }
                          >
                            Editar
                          </button>

                          {item.estadoAsignacion === "Activo" && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => finalizar(item)}
                            >
                              Finalizar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auditoria_ListarAsignacion;
